import { useEffect, useState, useMemo } from "react";
import {
  Calendar,
  FileDown,
  ChevronDown,
  Search as SearchIcon,
  Printer,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { getAllOrders } from "../../services/orderService";
import { getUserById } from "../../services/userService";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import InvoiceReceipt from "../../components/invoice/InvoiceReceipt";

// ─── View Modes ────────────────────────────────────────────
const VIEW_MODES = [
  { key: "referredBy", label: "Group by Referred By" },
  { key: "sponsoredBy", label: "Group by Sponsored By" },
  { key: "transactionType", label: "Group by Transaction Type" },
];

const TRANSACTION_TYPES = [
  "All",
  "TopUp Guest Shake",
  "TopUp Associate Shake",
  "Guest Shake",
  "Associate Shake",
  "Trial",
  "RetailShake",
  "Donation",
];

// ─── Helpers ───────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`;

const formatShortDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// ─── Transaction Row ───────────────────────────────────────
const TransactionRow = ({ txn, onViewInvoice }) => (
  <tr className="hover:bg-gray-50 transition-colors">
    <td className="px-3 py-2.5 text-[12px] font-mono text-gray-700">
      #{txn.orderId || "N/A"}
    </td>
    <td className="px-3 py-2.5 text-[11px] text-gray-600 whitespace-nowrap">
      {formatDate(txn.timestamp || txn.createdAt)}
    </td>
    <td className="px-3 py-2.5">
      <p className="text-[12px] font-semibold text-gray-800">{txn.userName || "Unknown"}</p>
      <p className="text-[10px] text-gray-500">{txn.userMobile || "N/A"}</p>
    </td>
    <td className="px-3 py-2.5">
      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded-full">
        {txn.transaction?.type || "N/A"}
      </span>
    </td>
    <td className="px-3 py-2.5 text-[11px] text-gray-600 max-w-[160px]">
      {txn.items?.map((item) => `${item.productName} (×${item.quantity})`).join(", ") || "N/A"}
    </td>
    <td className="px-3 py-2.5 text-center text-[12px] font-semibold text-blue-700">
      {txn.summary?.shakeBalanceChange ?? "—"}
    </td>
    <td className="px-3 py-2.5 text-center text-[12px] font-semibold text-green-700">
      {formatCurrency(txn.summary?.grandTotalAmount)}
    </td>
    <td className="px-3 py-2.5 text-center text-[12px] text-purple-700">
      {(txn.summary?.totalOrderVP || 0).toFixed(1)}
    </td>
    <td className="px-3 py-2.5 text-[11px] capitalize text-gray-600">
      {txn.transaction?.paymentMethod || "N/A"}
    </td>
    <td className="px-3 py-2.5">
      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
        txn.transaction?.status === "completed"
          ? "bg-green-50 text-green-700"
          : "bg-yellow-50 text-yellow-700"
      }`}>
        {txn.transaction?.status || "—"}
      </span>
    </td>
    <td className="px-3 py-2.5 text-center">
      <button
        onClick={() => onViewInvoice(txn)}
        className="text-[10px] px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-600 transition-colors"
      >
        View
      </button>
    </td>
  </tr>
);

// ─── Table Header ──────────────────────────────────────────
const TableHeader = () => (
  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
    <tr>
      {["Order ID", "Date & Time", "User Details", "Transaction Type", "Products",
        "Shake Qty", "Amount", "VP", "Payment", "Status", "Actions"].map((h, i) => (
        <th key={i} className="px-3 py-2.5 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">
          {h}
        </th>
      ))}
    </tr>
  </thead>
);

// ─── Group Summary Footer ──────────────────────────────────
const GroupFooter = ({ txns, colSpan = 11 }) => {
  const totalAmt = txns.reduce((s, t) => s + (t.summary?.grandTotalAmount || 0), 0);
  const totalVP = txns.reduce((s, t) => s + (t.summary?.totalOrderVP || 0), 0);
  const totalShakes = txns.reduce((s, t) => s + (t.summary?.shakeBalanceChange || 0), 0);
  return (
    <tr className="bg-slate-50 border-t-2 border-slate-200">
      <td colSpan={5} className="px-3 py-2 text-[11px] font-bold text-slate-600">
        Total ({txns.length} orders)
      </td>
      <td className="px-3 py-2 text-center text-[11px] font-bold text-blue-700">{totalShakes}</td>
      <td className="px-3 py-2 text-center text-[11px] font-bold text-green-700">{formatCurrency(totalAmt)}</td>
      <td className="px-3 py-2 text-center text-[11px] font-bold text-purple-700">{totalVP.toFixed(1)}</td>
      <td colSpan={3} />
    </tr>
  );
};

// ─── Main Component ────────────────────────────────────────
const ConsolidateReport = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [viewMode, setViewMode] = useState("referredBy");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransactionType, setSelectedTransactionType] = useState("All");
  const [selectedReferrer, setSelectedReferrer] = useState("All");
  const [selectedSponsor, setSelectedSponsor] = useState("All");
  const [dateFilter, setDateFilter] = useState("Today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async (filters = {}) => {
    setLoading(true);
    try {
      const orders = await getAllOrders(filters);
      const enriched = await Promise.all(
        orders.map(async (order) => {
          try {
            const user = await getUserById(order.userId);
            return {
              ...order,
              userName: user?.personalInformation?.fullName || user?.personalDetails?.name || "Unknown",
              userMobile: user?.personalInformation?.mobileNumber || user?.personalDetails?.mobile || "N/A",
              userEmail: user?.personalInformation?.email || user?.personalDetails?.email || "N/A",
              sponsoredBy: user?.sponsoredBy || user?.accountSecurity?.sponsoredBy || "N/A",
              referredBy: user?.accountSecurity?.referredBy || user?.sponsoredBy || "No Referrer",
              remainingShakes: user?.inventory?.remainingShakes || 0,
            };
          } catch {
            return { ...order, userName: "Unknown", userMobile: "N/A", referredBy: "No Referrer" };
          }
        })
      );
      setTransactions(enriched);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  // ── Date filter helper ──
  const getDateRange = (filter) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    switch (filter) {
      case "Today":
        return { startDate: today.toISOString(), endDate: new Date().toISOString() };
      case "This Week": {
        const ws = new Date(today);
        ws.setDate(today.getDate() - today.getDay());
        return { startDate: ws.toISOString(), endDate: new Date().toISOString() };
      }
      case "This Month": {
        const ms = new Date(today.getFullYear(), today.getMonth(), 1);
        return { startDate: ms.toISOString(), endDate: new Date().toISOString() };
      }
      case "Custom":
        return {
          startDate: fromDate ? new Date(fromDate).toISOString() : null,
          endDate: toDate ? new Date(toDate).toISOString() : null,
        };
      default:
        return {};
    }
  };

  const handleSearch = () => {
    const dateRange = getDateRange(dateFilter);
    fetchTransactions({
      ...dateRange,
      transactionType: selectedTransactionType !== "All" ? selectedTransactionType : null,
    });
    toast.success("Filters applied");
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedTransactionType("All");
    setSelectedReferrer("All");
    setSelectedSponsor("All");
    setDateFilter("All");
    setFromDate("");
    setToDate("");
    fetchTransactions();
  };

  // ── Unique referrers & sponsors (for dropdowns) ──
  const uniqueReferrers = useMemo(() => {
    const vals = [...new Set(transactions.map(t => t.referredBy || "No Referrer").filter(Boolean))];
    return vals.sort((a, b) => a.localeCompare(b));
  }, [transactions]);

  const uniqueSponsors = useMemo(() => {
    const vals = [...new Set(transactions.map(t => t.sponsoredBy).filter(v => v && v !== "N/A"))];
    return vals.sort((a, b) => a.localeCompare(b));
  }, [transactions]);

  // ── Client-side filtering ──
  const filteredTransactions = useMemo(() => {
    return transactions.filter((txn) => {
      const searchLow = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        txn.orderId?.toLowerCase().includes(searchLow) ||
        txn.userName?.toLowerCase().includes(searchLow) ||
        txn.userMobile?.includes(searchTerm) ||
        txn.referredBy?.toLowerCase().includes(searchLow);

      const matchesType =
        selectedTransactionType === "All" ||
        txn.transaction?.type === selectedTransactionType;

      const matchesReferrer =
        selectedReferrer === "All" || txn.referredBy === selectedReferrer;

      const matchesSponsor =
        selectedSponsor === "All" || txn.sponsoredBy === selectedSponsor;

      // Client-side date filter
      if (dateFilter === "All") return matchesSearch && matchesType && matchesReferrer && matchesSponsor;
      const txnDate = new Date(txn.timestamp || txn.createdAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let matchesDate = true;
      if (dateFilter === "Today") matchesDate = txnDate >= today;
      else if (dateFilter === "This Week") {
        const ws = new Date(today);
        ws.setDate(today.getDate() - today.getDay());
        matchesDate = txnDate >= ws;
      } else if (dateFilter === "This Month") {
        matchesDate = txnDate >= new Date(today.getFullYear(), today.getMonth(), 1);
      } else if (dateFilter === "Custom") {
        if (fromDate) matchesDate = matchesDate && txnDate >= new Date(fromDate);
        if (toDate) {
          const end = new Date(toDate);
          end.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && txnDate <= end;
        }
      }

      return matchesSearch && matchesType && matchesReferrer && matchesSponsor && matchesDate;
    });
  }, [transactions, searchTerm, selectedTransactionType, selectedReferrer, selectedSponsor, dateFilter, fromDate, toDate]);

  // ── Grouping logic ──
  const grouped = useMemo(() => {
    if (viewMode === "referredBy") {
      // Group by referredBy → then by transactionType (nested)
      const map = {};
      filteredTransactions.forEach((t) => {
        const ref = t.referredBy || "No Referrer";
        const type = t.transaction?.type || "Unknown";
        if (!map[ref]) map[ref] = {};
        if (!map[ref][type]) map[ref][type] = [];
        map[ref][type].push(t);
      });
      return Object.entries(map)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([ref, typeMap]) => ({
          groupLabel: ref,
          subGroups: Object.entries(typeMap).sort((a, b) => a[0].localeCompare(b[0])),
        }));
    }

    if (viewMode === "sponsoredBy") {
      // Group by sponsoredBy → then by transactionType (nested)
      const map = {};
      filteredTransactions.forEach((t) => {
        const sponsor = t.sponsoredBy || "No Sponsor";
        const type = t.transaction?.type || "Unknown";
        if (!map[sponsor]) map[sponsor] = {};
        if (!map[sponsor][type]) map[sponsor][type] = [];
        map[sponsor][type].push(t);
      });
      return Object.entries(map)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([sponsor, typeMap]) => ({
          groupLabel: sponsor,
          subGroups: Object.entries(typeMap).sort((a, b) => a[0].localeCompare(b[0])),
        }));
    }

    if (viewMode === "transactionType") {
      // Group by transaction type only
      const map = {};
      filteredTransactions.forEach((t) => {
        const key = t.transaction?.type || "Unknown";
        if (!map[key]) map[key] = [];
        map[key].push(t);
      });
      return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
    }
    return [];
  }, [filteredTransactions, viewMode]);

  const toggleGroup = (key) => setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Grand totals ──
  const grandTotals = useMemo(() => ({
    count: filteredTransactions.length,
    amount: filteredTransactions.reduce((s, t) => s + (t.summary?.grandTotalAmount || 0), 0),
    vp: filteredTransactions.reduce((s, t) => s + (t.summary?.totalOrderVP || 0), 0),
    shakes: filteredTransactions.reduce((s, t) => s + (t.summary?.shakeBalanceChange || 0), 0),
  }), [filteredTransactions]);

  // ── Export to Excel ──
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const rows = [];
    const startStr = fromDate ? formatShortDate(fromDate) : formatShortDate(new Date().toISOString());
    const endStr = toDate ? formatShortDate(toDate) : formatShortDate(new Date().toISOString());

    rows.push([`Consolidated Report  From: ${startStr}  To: ${endStr}`]);
    rows.push([]);
    rows.push(["Group", "Order ID", "Date", "User", "Transaction Type", "Products", "Shake Qty", "Amount", "VP", "Payment", "Status"]);

    const isNestedMode = viewMode === "referredBy" || viewMode === "sponsoredBy";

    if (isNestedMode) {
      grouped.forEach(({ groupLabel, subGroups }) => {
        rows.push([`▶ ${groupLabel.toUpperCase()}`, "", "", "", "", "", "", "", "", "", ""]);
        subGroups.forEach(([type, txns]) => {
          rows.push(["", `  ── ${type} ──`, "", "", "", "", "", "", "", "", ""]);
          txns.forEach((t) => {
            rows.push([
              groupLabel,
              t.orderId || "N/A",
              formatShortDate(t.timestamp || t.createdAt),
              t.userName || "N/A",
              t.transaction?.type || "N/A",
              t.items?.map((i) => `${i.productName}(×${i.quantity})`).join(", ") || "",
              t.summary?.shakeBalanceChange ?? "",
              parseFloat((t.summary?.grandTotalAmount || 0).toFixed(2)),
              parseFloat((t.summary?.totalOrderVP || 0).toFixed(2)),
              t.transaction?.paymentMethod || "N/A",
              t.transaction?.status || "N/A",
            ]);
          });
          const sub = txns.reduce((s, t) => s + (t.summary?.grandTotalAmount || 0), 0);
          rows.push(["", `  Subtotal (${txns.length})`, "", "", "", "", "", parseFloat(sub.toFixed(2)), "", "", ""]);
        });
        rows.push([]);
      });
    } else {
      // transactionType mode — flat [key, txns] tuples
      grouped.forEach(([groupKey, txns]) => {
        rows.push([`▶ ${groupKey.toUpperCase()}`, "", "", "", "", "", "", "", "", "", ""]);
        txns.forEach((t) => {
          rows.push([
            t.referredBy || "N/A",
            t.orderId || "N/A",
            formatShortDate(t.timestamp || t.createdAt),
            t.userName || "N/A",
            t.transaction?.type || "N/A",
            t.items?.map((i) => `${i.productName}(×${i.quantity})`).join(", ") || "",
            t.summary?.shakeBalanceChange ?? "",
            parseFloat((t.summary?.grandTotalAmount || 0).toFixed(2)),
            parseFloat((t.summary?.totalOrderVP || 0).toFixed(2)),
            t.transaction?.paymentMethod || "N/A",
            t.transaction?.status || "N/A",
          ]);
        });
        const sub = txns.reduce((s, t) => s + (t.summary?.grandTotalAmount || 0), 0);
        rows.push([`Group Total (${txns.length})`, "", "", "", "", "", "", parseFloat(sub.toFixed(2)), "", "", ""]);
        rows.push([]);
      });
    }

    rows.push(["===", "===", "===", "===", "===", "===", "===", "===", "===", "===", "==="]);
    rows.push([`Grand Total (${grandTotals.count} orders)`, "", "", "", "", "", grandTotals.shakes, parseFloat(grandTotals.amount.toFixed(2)), parseFloat(grandTotals.vp.toFixed(2)), "", ""]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [14, 12, 14, 22, 20, 36, 12, 12, 10, 14, 12].map((w) => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, "Consolidated Report");
    XLSX.writeFile(wb, `Consolidated_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exported to Excel");
    setShowExportMenu(false);
  };

  // ── Export to PDF ──
  const exportToPDF = () => {
    const doc = new jsPDF("landscape");
    const pageW = doc.internal.pageSize.getWidth();
    const startStr = fromDate ? formatShortDate(fromDate) : formatShortDate(new Date().toISOString());
    const endStr = toDate ? formatShortDate(toDate) : formatShortDate(new Date().toISOString());
    const RED = [180, 0, 0];
    const NAVY = [15, 23, 42];

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Consolidated Shake Report", pageW / 2, 14, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(80);
    doc.text(`Period: ${startStr} - ${endStr}  |  View: ${VIEW_MODES.find(v => v.key === viewMode)?.label}`, pageW / 2, 20, { align: "center" });
    doc.setDrawColor(200);
    doc.setLineWidth(0.3);
    doc.line(10, 23, pageW - 10, 23);

    const body = [];
    const heads = [["Group / Ref.", "Order ID", "Date", "User", "Trans. Type", "Products", "Shakes", "Amount", "VP", "Payment", "Status"]];

    const addGroupRows = (groupLabel, txns) => {
      body.push([{ content: `>> ${groupLabel.toUpperCase()}`, colSpan: 11, styles: { fontStyle: "bold", textColor: RED, fillColor: [255, 248, 248], fontSize: 8 } }]);
      txns.forEach((t) => {
        body.push([
          t.referredBy || "N/A",
          t.orderId || "N/A",
          formatShortDate(t.timestamp || t.createdAt),
          t.userName || "N/A",
          t.transaction?.type || "N/A",
          t.items?.map((i) => `${i.productName}(×${i.quantity})`).join(", ") || "",
          { content: String(t.summary?.shakeBalanceChange ?? ""), styles: { halign: "center" } },
          { content: (t.summary?.grandTotalAmount || 0).toFixed(2), styles: { halign: "right" } },
          { content: (t.summary?.totalOrderVP || 0).toFixed(1), styles: { halign: "right" } },
          t.transaction?.paymentMethod || "N/A",
          t.transaction?.status || "N/A",
        ]);
      });
      const sub = txns.reduce((s, t) => s + (t.summary?.grandTotalAmount || 0), 0);
      body.push([
        { content: `Group Total (${txns.length})`, colSpan: 7, styles: { fontStyle: "bold", textColor: RED } },
        { content: sub.toFixed(2), colSpan: 4, styles: { fontStyle: "bold", textColor: RED, halign: "right" } },
      ]);
      body.push([{ content: "", colSpan: 11, styles: { fillColor: [235, 235, 240], minCellHeight: 2, cellPadding: 0 } }]);
    };

    const isNestedMode = viewMode === "referredBy" || viewMode === "sponsoredBy";

    if (isNestedMode) {
      grouped.forEach(({ groupLabel, subGroups }) => {
        body.push([{ content: `## ${groupLabel.toUpperCase()}`, colSpan: 11, styles: { fontStyle: "bold", textColor: NAVY, fillColor: [240, 240, 255], fontSize: 9 } }]);
        subGroups.forEach(([type, txns]) => addGroupRows(type, txns));
      });
    } else {
      // transactionType mode — flat [key, txns] tuples
      grouped.forEach(([key, txns]) => addGroupRows(key, txns));
    }

    // Grand total
    body.push([
      { content: `GRAND TOTAL  (${grandTotals.count} orders)`, colSpan: 6, styles: { fontStyle: "bold", textColor: [255, 255, 255], fillColor: NAVY, fontSize: 8.5 } },
      { content: String(grandTotals.shakes), styles: { fontStyle: "bold", textColor: [255, 255, 255], fillColor: NAVY, halign: "center" } },
      { content: grandTotals.amount.toFixed(2), styles: { fontStyle: "bold", textColor: [255, 255, 255], fillColor: NAVY, halign: "right" } },
      { content: grandTotals.vp.toFixed(1), styles: { fontStyle: "bold", textColor: [255, 255, 255], fillColor: NAVY, halign: "right" } },
      { content: "", colSpan: 2, styles: { fillColor: NAVY } },
    ]);

    autoTable(doc, {
      head: heads,
      body,
      startY: 27,
      margin: { left: 8, right: 8 },
      tableWidth: pageW - 16,
      theme: "plain",
      styles: { fontSize: 7, cellPadding: { top: 2, bottom: 2, left: 2.5, right: 2.5 }, textColor: [30, 30, 30], lineColor: [220, 220, 220], overflow: "linebreak" },
      headStyles: { fontStyle: "bold", fillColor: [255, 255, 255], textColor: [0, 0, 0], lineWidth: { bottom: 0.4 }, cellPadding: { top: 3.5, bottom: 3.5, left: 2.5, right: 2.5 } },
      alternateRowStyles: { fillColor: [250, 250, 252] },
      columnStyles: { 0: { cellWidth: 26 }, 1: { cellWidth: 16 }, 2: { cellWidth: 20 }, 3: { cellWidth: 34 }, 4: { cellWidth: 26 }, 5: { cellWidth: 58 }, 6: { cellWidth: 13 }, 7: { cellWidth: 20 }, 8: { cellWidth: 12 }, 9: { cellWidth: 22 }, 10: { cellWidth: 19 } },
      didDrawPage: (data) => {
        const pg = doc.internal.getCurrentPageInfo().pageNumber;
        const total = doc.internal.getNumberOfPages();
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(`Page ${pg} / ${total}`, pageW - 8, doc.internal.pageSize.getHeight() - 6, { align: "right" });
        doc.text(`Generated: ${new Date().toLocaleDateString("en-IN")}`, 8, doc.internal.pageSize.getHeight() - 6);
      },
    });

    doc.save(`Consolidated_Report_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF Exported");
    setShowExportMenu(false);
  };

  // ─── Render Grouped Tables ─────────────────────────────────
  const renderTable = (txns, label, key) => {
    const isCollapsed = collapsedGroups[key];
    const groupAmount = txns.reduce((s, t) => s + (t.summary?.grandTotalAmount || 0), 0);
    return (
      <div key={key} className="mb-3 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm print:break-inside-avoid">
        {/* Group Header */}
        <button
          onClick={() => toggleGroup(key)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-700 text-white hover:from-slate-700 print:pointer-events-none"
        >
          <span className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-wide">
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4 rotate-90" />}
            {label}
          </span>
          <span className="text-[11px] text-slate-300 font-medium">
            {txns.length} orders · {formatCurrency(groupAmount)}
          </span>
        </button>

        {/* Table */}
        {!isCollapsed && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <TableHeader />
              <tbody className="divide-y divide-gray-100">
                {txns.map((txn) => (
                  <TransactionRow key={txn.id || txn.orderId} txn={txn} onViewInvoice={setSelectedInvoice} />
                ))}
              </tbody>
              <tfoot>
                <GroupFooter txns={txns} />
              </tfoot>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderNestedGroupMode = () =>
    grouped.map(({ groupLabel, subGroups }) => {
      const allTxns = subGroups.flatMap(([, txns]) => txns);
      const refTotal = allTxns.reduce((s, t) => s + (t.summary?.grandTotalAmount || 0), 0);
      return (
        <div key={groupLabel} className="mb-6">
          {/* Group banner */}
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-1 h-px bg-slate-300" />
            <div className="flex flex-col items-center">
              <span className="px-5 py-1.5 bg-slate-900 text-white text-[13px] font-black uppercase tracking-widest rounded-full shadow-md">
                {groupLabel}
              </span>
              <span className="text-[10px] text-slate-500 mt-1">
                {allTxns.length} orders · {formatCurrency(refTotal)}
              </span>
            </div>
            <div className="flex-1 h-px bg-slate-300" />
          </div>
          {subGroups.map(([type, txns]) =>
            renderTable(txns, type, `${groupLabel}__${type}`)
          )}
        </div>
      );
    });

  const renderFlatMode = () =>
    grouped.map(([key, txns]) => renderTable(txns, key, key));

  return (
    <div className="max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-gray-50 min-h-screen">
      {/* ── Header ── */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Consolidated Report</h1>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Grouped by {VIEW_MODES.find((v) => v.key === viewMode)?.label} · {grandTotals.count} orders · {formatCurrency(grandTotals.amount)}
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-3 h-9 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all text-[12px] font-medium"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center gap-2 px-3 h-9 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all text-[12px] font-medium"
            >
              <FileDown className="w-4 h-4" />
              Export
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20">
                <button onClick={exportToExcel} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-[12px] first:rounded-t-xl">
                  Export Excel
                </button>
                <button onClick={exportToPDF} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-[12px] last:rounded-b-xl">
                  Export PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── View Mode Tabs ── */}
      <div className="flex gap-1 mb-4 bg-white rounded-xl border border-gray-200 p-1 w-fit print:hidden">
        {VIEW_MODES.map((m) => (
          <button
            key={m.key}
            onClick={() => setViewMode(m.key)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
              viewMode === m.key
                ? "bg-slate-800 text-white shadow"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-4 print:hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
          {/* Search */}
          <div className="col-span-2 sm:col-span-1 xl:col-span-2">
            <label className="block text-[10px] font-semibold text-gray-600 mb-1 uppercase tracking-wide">Search</label>
            <div className="relative">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Name, mobile..."
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-[12px] outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Referred By filter */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1 uppercase tracking-wide">Referred By</label>
            <select
              value={selectedReferrer}
              onChange={(e) => setSelectedReferrer(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[12px] outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="All">All Referrers</option>
              {uniqueReferrers.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Sponsored By filter */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1 uppercase tracking-wide">Sponsored By</label>
            <select
              value={selectedSponsor}
              onChange={(e) => setSelectedSponsor(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[12px] outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="All">All Sponsors</option>
              {uniqueSponsors.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Transaction Type */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1 uppercase tracking-wide">Trans. Type</label>
            <select
              value={selectedTransactionType}
              onChange={(e) => setSelectedTransactionType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[12px] outline-none focus:ring-2 focus:ring-blue-500"
            >
              {TRANSACTION_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-600 mb-1 uppercase tracking-wide">Date</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-[12px] outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="All">All Time</option>
              <option value="Today">Today</option>
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
              <option value="Custom">Custom Range</option>
            </select>
          </div>

          {/* From / To */}
          {dateFilter === "Custom" && (
            <>
              <div>
                <label className="block text-[10px] font-semibold text-gray-600 mb-1 uppercase tracking-wide">From</label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg text-[12px] outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-600 mb-1 uppercase tracking-wide">To</label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg text-[12px] outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            </>
          )}

          {/* Buttons */}
          <div className="flex items-end gap-2">
            <button onClick={handleSearch} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-[12px] font-semibold hover:bg-red-700 transition-all">
              Search
            </button>
            <button onClick={handleClearFilters} className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-[12px] font-semibold hover:bg-gray-200 transition-all">
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* ── Grand Summary Bar ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { label: "Total Orders", value: grandTotals.count, color: "bg-slate-800 text-white" },
          { label: "Total Amount", value: formatCurrency(grandTotals.amount), color: "bg-green-600 text-white" },
          { label: "Total VP", value: grandTotals.vp.toFixed(1), color: "bg-purple-600 text-white" },
          { label: "Shake Qty", value: grandTotals.shakes, color: "bg-blue-600 text-white" },
        ].map((s) => (
          <div key={s.label} className={`${s.color} rounded-xl p-3 text-center shadow-sm`}>
            <p className="text-[10px] opacity-80 uppercase tracking-wide font-semibold">{s.label}</p>
            <p className="text-lg font-black mt-0.5">{s.value}</p>
          </div>
        ))}
      </div>

      {/* ── Tables ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading transactions...</p>
          </div>
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <p className="text-gray-400 text-sm font-medium">No transactions found.</p>
          <p className="text-gray-300 text-xs mt-1">Try adjusting your filters or date range.</p>
        </div>
      ) : viewMode === "referredBy" || viewMode === "sponsoredBy" ? (
        renderNestedGroupMode()
      ) : (
        renderFlatMode()
      )}

      {/* ── Invoice Modal ── */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedInvoice(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800">Invoice #{selectedInvoice.orderId}</h3>
              <button onClick={() => setSelectedInvoice(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            </div>
            <div className="p-4">
              <InvoiceReceipt transaction={selectedInvoice} onClose={() => setSelectedInvoice(null)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsolidateReport;
